import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { GradientText } from "./GradientText";
import { ArrowRight, Calendar } from "lucide-react";

/**
 * FinalCTA
 *
 * Bottom-of-page call-to-action section with:
 * - Two CTA buttons
 * - Entrance animations
 * - All text from translations
 */
export function FinalCTA() {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const headline = t("finalCTA.headline");
  const description = t("finalCTA.description");
  const primaryCTAText = t("finalCTA.primaryCTA");
  const secondaryCTAText = t("finalCTA.secondaryCTA");

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={cn(
            "mx-auto max-w-3xl text-center",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          {/* Headline */}
          <h2 className="mb-6 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{headline}</GradientText>
          </h2>

          {/* Description */}
          <p className="mx-auto mb-10 max-w-xl text-base text-muted-foreground lg:text-lg">
            {description}
          </p>

          {/* CTAs */}
          <div
            className={cn(
              "flex flex-col items-center justify-center gap-4 sm:flex-row",
              "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
              isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
            )}
            style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
          >
            <Link to="/signup">
              <Button
                size="lg"
                className={cn("h-12 px-8 text-base", "transition-all duration-300")}
              >
                {primaryCTAText}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>

            {secondaryCTAText && (
              <Button
                variant="ghost"
                size="lg"
                className={cn(
                  "h-12 px-8 text-base",
                  "text-foreground hover:text-foreground",
                  "hover:bg-muted",
                  "transition-all duration-300",
                )}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {secondaryCTAText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
